// src/components/CraftingExcellence.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  Image,
  VStack,
  HStack,
  Icon,
  useColorModeValue,
  Circle,
  AspectRatio,
} from '@chakra-ui/react';
import { keyframes } from '@chakra-ui/system';
import { ArrowForwardIcon, ArrowBackIcon } from '@chakra-ui/icons';
import { Link as RouterLink } from 'react-router-dom';
import group_photo from '../assets/group_photo.jpg';
import group_photo2 from '../assets/edris_group.jpeg';
import city_court from '../assets/city_court.webp';
import building_2 from '../assets/building_2.png';
import agerfa from '../assets/agarfa.webp';
import baleMan from '../assets/bale_man_clear.jpeg';
import edris_2 from '../assets/edris_2.jpg';
import gallery_9 from '../assets/gallery_9.jpg';

const images = [group_photo2];

const fadeIn = keyframes`
  from { opacity: 0.4; }
  to { opacity: 1; }
`;

const LearnMoreButton = () => {
  return (
    <RouterLink to="/about">
      <HStack
        as="button"
        spacing={0}
        p="2px"
        borderRadius="full"
        bg={useColorModeValue('gray.100', 'gray.700')}
        transition="all 0.2s"
        _hover={{ boxShadow: 'md' }}
      >
        <Box as="span" px={5} py={2} bg="orange.500" color="white" borderRadius="full" fontWeight="semibold">
          Learn More
        </Box>
        <Circle bg="blue.900" size="40px" ml="-12px" zIndex={1}>
          <Icon as={ArrowForwardIcon} color="white" />
        </Circle>
      </HStack>
    </RouterLink>
  );
};

const missionSlides = [
  {
    title: 'Our Mission',
    text: 'Our mission is to deliver exceptional construction services by combining innovative technology and advanced techniques with a steadfast commitment to unparalleled safety and quality. We are dedicated to forging lasting client partnerships built on a foundation of transparency and open communication, empowering our skilled team to ensure every project not only meets its intended goals but also exceeds expectations and stands as a proud landmark for the community it serves.',
  },
  {
    title: 'Our Vision',
    text: 'Our vision is to be the region\'s leading and most respected construction partner, setting the definitive industry benchmark for innovation, sustainability, and operational excellence. We aspire to build not just remarkable structures, but also a lasting legacy of progress and trust that enriches communities and improves infrastructure for future generations, making us the undisputed first choice for clients who seek reliability and extraordinary results.',
  },
  {
    title: 'Our Values',
    text: 'Our work is anchored by the fundamental values that form the bedrock of our company culture and every decision we make. Integrity demands unwavering honesty and accountability in every action we take. Excellence drives us to deliver superior craftsmanship and uncompromising quality. Safety is our non-negotiable commitment to the well-being of our people and partners. And Collaboration empowers us to work as one unified team, turning complex challenges into shared successes.',
  },
];

export default function CraftingExcellence() {
  const boxBg = '#0A1A44';
  const textColor = 'white';

  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 4000);

    return () => clearInterval(intervalId);
  }, []);

  const handlePrev = () => {
    const newIndex = (currentIndex - 1 + missionSlides.length) % missionSlides.length;
    setCurrentIndex(newIndex);
  };
  
  const handleNext = () => {
    const newIndex = (currentIndex + 1) % missionSlides.length;
    setCurrentIndex(newIndex);
  };

  const imageContent = (
    <Box position="relative" w="full" h="full">
      <Image
        key={currentImageIndex}
        src={images[currentImageIndex]}
        alt="Our team at work"
        position="absolute"
        top="0"
        left="0"
        w="100%"
        h="100%"
        objectFit="cover"
        animation={`${fadeIn} 1s ease-in-out`}
      />
      <Box
        position="absolute"
        bottom="0"
        left="0"
        bg="orange.500"
        color="white"
        px={6}
        py={3}
        borderRadius={{ tl: 0, tr: 'lg', bl: 0, br: 'lg' }}
      >
        <Text fontSize="xl" fontWeight="bold">
          15+ <Text as="span" fontSize="sm" fontWeight="normal">Years of Experience</Text>
        </Text>
      </Box>
    </Box>
  );

  return (
    <Box as="section" py={{ base: 16, md: 24 }} bg={useColorModeValue('white', 'gray.800')} id="about">
      <VStack maxW="7xl" mx="auto" px={8} spacing={12} align="flex-start">
        {/* ... Top section remains the same ... */}
        <Flex
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
          align="center"
          w="full"
        >
          <VStack align="flex-start" spacing={5} maxW="lg">
            <Text color="orange.500" fontWeight="semibold">
              Who We Are
            </Text>
            <Heading as="h2" size="2xl" fontWeight="extrabold" lineHeight="shorter">
              Crafting Excellence in Every Project
            </Heading>
            <Text color="gray.600" fontSize="lg">
              We believe that great projects are built on a foundation of strong partnerships. From concept to completion, we work with you every step of the way.
            </Text>
          </VStack>
          <Box mt={{ base: 6, md: 0 }}>
            <LearnMoreButton />
          </Box>
        </Flex>

        <Flex
          direction={{ base: 'column', lg: 'row' }}
          align={{ base: 'center', lg: 'stretch' }}
          gap={12}
          w="full"
        >
          {/* --- UPDATED Left Side --- */}
          <Box flex="1" w="full">
            <Box display={{ base: 'block', lg: 'none' }}>
              <AspectRatio ratio={4 / 3} borderRadius="xl" overflow="hidden">
                {imageContent}
              </AspectRatio>
            </Box>
            <Box display={{ base: 'none', lg: 'block' }} h="full">
              <Box borderRadius="xl" overflow="hidden" h="full">
                {imageContent}
              </Box>
            </Box>
          </Box>

          {/* Right Side: Slidable Content */}
          <Flex
            direction="column"
            justify="space-between"
            flex="1"
            bg={boxBg}
            color={textColor}
            p={10}
            borderRadius="2xl"
          >
            {/* ... Right side content remains the same ... */}
            <VStack align="flex-start" spacing={5}>
              <Text fontSize="lg" lineHeight="tall" color="gray.200">
                {missionSlides[currentIndex].text}
              </Text>
              <Text as="h3" fontSize="xl" fontWeight="bold" color="orange.400">
                {missionSlides[currentIndex].title}
              </Text>
            </VStack>
            <HStack spacing={4} alignSelf="flex-end" mt={8}>
              <Button onClick={handlePrev} variant="outline" borderColor="whiteAlpha.400" color="white" isRound>
                <Icon as={ArrowBackIcon} />
              </Button>
              <Button onClick={handleNext} bg="orange.500" color="white" isRound _hover={{ bg: 'orange.600' }}>
                <Icon as={ArrowForwardIcon} />
              </Button>
            </HStack>
          </Flex>
        </Flex>
      </VStack>
    </Box>
  );
}